/**
 * Singleton STOMP + SockJS client.
 *
 * Architecture:
 * - One client instance for the entire authenticated session.
 * - Subscription configs are retained so destinations are re-subscribed after reconnect.
 * - Duplicate subscriptions to the same destination are prevented via a destination-keyed map.
 */
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { SOCKET_ENDPOINT_URL } from "./socketConfig";
import {
  buildSocketAuthHeaders,
  createAuthenticatedSockJS,
  getSocketAuthToken,
  installSockJsXhrAuthInterceptor,
  uninstallSockJsXhrAuthInterceptor,
} from "./socketAuth";

const RECONNECT_DELAY_MS = 5000;
const HEARTBEAT_MS = 10000;

class SocketService {
  constructor() {
    this.client = null;
    this.status = "disconnected";
    this.onStatusChange = null;
    this.onConnected = null;
    this.intentionalDisconnect = false;
    /** @type {Map<string, { callback: Function, stompSubscription: object|null }>} */
    this.subscriptionRegistry = new Map();
  }

  /**
   * @param {{
   *   onStatusChange?: (status: string) => void,
   *   onConnected?: () => void,
   * }} [options]
   */
  connect(options = {}) {
    const token = getSocketAuthToken();
    if (!token) {
      return false;
    }

    this.onStatusChange = options.onStatusChange || null;
    this.onConnected = options.onConnected || null;
    this.intentionalDisconnect = false;

    if (this.client?.connected) {
      this._setStatus("connected");
      this.onConnected?.();
      return true;
    }

    if (this.client?.active) {
      this.client.deactivate();
      this.client = null;
    }

    installSockJsXhrAuthInterceptor();
    this._setStatus("connecting");

    this.client = new Client({
      webSocketFactory: () =>
        createAuthenticatedSockJS(
          SOCKET_ENDPOINT_URL,
          SockJS,
          getSocketAuthToken(),
        ),
      reconnectDelay: RECONNECT_DELAY_MS,
      heartbeatIncoming: HEARTBEAT_MS,
      heartbeatOutgoing: HEARTBEAT_MS,
      connectHeaders: buildSocketAuthHeaders(token),
      beforeConnect: () => {
        const freshToken = getSocketAuthToken();
        if (this.client && freshToken) {
          this.client.connectHeaders = buildSocketAuthHeaders(freshToken);
        }
      },
      onConnect: () => {
        this._setStatus("connected");
        this._resubscribeAll();
        this.onConnected?.();
      },
      onDisconnect: () => {
        this._clearActiveStompSubscriptions();
        if (this.intentionalDisconnect || !this.client?.active) {
          this._setStatus("disconnected");
        } else {
          this._setStatus("connecting");
        }
      },
      onStompError: () => {
        if (!this.intentionalDisconnect && this.client?.active) {
          this._setStatus("connecting");
        } else {
          this._setStatus("disconnected");
        }
      },
      onWebSocketClose: () => {
        if (this.intentionalDisconnect) {
          this._setStatus("disconnected");
          return;
        }

        if (this.client?.active) {
          this._setStatus("connecting");
        } else {
          this._setStatus("disconnected");
        }
      },
      onWebSocketError: () => {
        if (!this.intentionalDisconnect && this.client?.active) {
          this._setStatus("connecting");
        }
      },
    });

    this.client.activate();
    return true;
  }

  disconnect() {
    this.intentionalDisconnect = true;
    this.onConnected = null;
    this.subscriptionRegistry.clear();
    this._clearActiveStompSubscriptions();

    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }

    this._setStatus("disconnected");
    this.onStatusChange = null;
    uninstallSockJsXhrAuthInterceptor();
  }

  /**
   * Subscribe to a STOMP destination. Only one subscription per destination is kept.
   *
   * @param {string} destination
   * @param {(body: string) => void} callback
   * @returns {() => void} cleanup function
   */
  subscribe(destination, callback) {
    const existing = this.subscriptionRegistry.get(destination);

    if (existing) {
      existing.callback = callback;
      if (this.isConnected()) {
        this._attachStompSubscription(destination);
      }
      return () => this.unsubscribe(destination);
    }

    this.subscriptionRegistry.set(destination, {
      callback,
      stompSubscription: null,
    });

    if (this.isConnected()) {
      this._attachStompSubscription(destination);
    }

    return () => this.unsubscribe(destination);
  }

  unsubscribe(destination) {
    const entry = this.subscriptionRegistry.get(destination);
    if (!entry) {
      return;
    }

    entry.stompSubscription?.unsubscribe?.();
    this.subscriptionRegistry.delete(destination);
  }

  /**
   * @param {string} destination
   * @param {object} body
   */
  publish(destination, body) {
    if (!this.isConnected() || !this.client) {
      return;
    }

    this.client.publish({
      destination,
      body: JSON.stringify(body),
    });
  }

  isConnected() {
    return this.status === "connected" && Boolean(this.client?.connected);
  }

  getStatus() {
    return this.status;
  }

  _setStatus(status) {
    this.status = status;
    this.onStatusChange?.(status);
  }

  _attachStompSubscription(destination) {
    const entry = this.subscriptionRegistry.get(destination);
    if (!entry || !this.client?.connected) {
      return;
    }

    entry.stompSubscription?.unsubscribe?.();
    entry.stompSubscription = this.client.subscribe(destination, (message) => {
      entry.callback?.(message.body);
    });
  }

  _resubscribeAll() {
    this.subscriptionRegistry.forEach((_, destination) => {
      this._attachStompSubscription(destination);
    });
  }

  _clearActiveStompSubscriptions() {
    this.subscriptionRegistry.forEach((entry) => {
      entry.stompSubscription?.unsubscribe?.();
      entry.stompSubscription = null;
    });
  }
}

const socketService = new SocketService();

export default socketService;
